import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { AuthService } from '@app/core/domain/auth';
import { PortalStorageService } from '@app/core/services';
import { AdminService } from '../../domain/admin/services/admin.service';
import { PortalUserRole, PortalUserDto } from '../../domain/admin/models/portalUserDto';
import { intersactArray } from '../util/data-helper';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService,
        private storageService: PortalStorageService,
        private admingService: AdminService,
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        const refreshToken = route.queryParamMap.get('refreshToken');
        if (typeof refreshToken !== undefined && refreshToken !== null) {
            this.storageService.saveRefreshToken(refreshToken);
            //hide the nav bar if the auth by refresh token
            this.storageService.saveNav('true');
        }

        const theme = route.queryParamMap.get('theme');
        if (typeof theme !== undefined && theme !== null) {
            this.storageService.saveTheme(theme);
        }
        const hidenav = route.queryParamMap.get('hidenav');
        if (typeof hidenav !== undefined && hidenav !== null) {
            this.storageService.saveNav(hidenav);
        }
        const username = route.queryParamMap.get('user');
        if (typeof username !== undefined && username !== null) {
            this.storageService.saveUserName(username);
        }

        const aToken = route.queryParamMap.get('accesstoken');
        const userId = route.queryParamMap.get('userid');

        if (aToken !== null && userId !== null) {
            this.storageService.clearToken();
            this.storageService.saveAccessToken(aToken);
            this.storageService.saveUserId(userId);
        }

        if (refreshToken == null) {
            return this.authenticate(route, state);
        } else {
            this.routeToLogin(state);
            return false;
        }
    }

    private routeToLogin(state: RouterStateSnapshot): void {
        // not logged in so redirect to login page with the return url
        this.router.navigate(['/login'], {
            queryParams: { returnUrl: state.url.split('?')[0] },
        });
    }

    private authenticate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        if (this.authService.isAuthenticated()) {
            // logged in so return true
            //Get user and check if the user is authorized to access this route based on the 'unauthorized' field we pass in the app-routing module.
            let storedUser = this.storageService.getUser();
            const token = this.storageService.getToken();
            if ((storedUser == null && storedUser == undefined) || (storedUser != null && storedUser.portalUserId != token.userId)) {
                let portalUserId = 0;
                if (this.authService.tokenValue !== null) {
                    portalUserId = this.authService.tokenValue.userId;
                }
                async () => {
                    storedUser = await this.admingService.getPortalUserAsync(portalUserId);
                    this.storageService.saveUser(storedUser);
                    const roles = this.getPortalUserRoles(storedUser);
                    if (route.data.unauthorized) {
                        const array = intersactArray(route.data.unauthorized, roles);
                        if (array.length > 0) {
                            this.router.navigate(['**']);
                            return false;
                        }
                    }
                };
            } else {
                const roles = this.getPortalUserRoles(storedUser);
                if (route.data.unauthorized) {
                    const array = intersactArray(route.data.unauthorized, roles);
                    if (array.length > 0) {
                        this.router.navigate(['**']);
                        return false;
                    }
                }
            }

            // this is for allowing only authorized roles
            // if (route.data.authorized && route.data.authorized.indexOf(loggedInUser.appUserTypeId) == -1) {
            //     this.router.navigate(['**']);
            //     return false;
            // }
            return true;
        } else {
            this.routeToLogin(state);
            return false;
        }
    }

    getPortalUserRoles(user: PortalUserDto): PortalUserRole[] {
        const roles = [];
        if (user.vendor) {
            roles.push(PortalUserRole.Vendor);
        }
        if (user.canScheduleCase != null && user.canScheduleCase) {
            roles.push(PortalUserRole.CanScheduleCase);
        }
        if (user.canConfirmCase != null && user.canConfirmCase) {
            roles.push(PortalUserRole.CanConfirmCase);
        }
        return roles;
    }
}
